/*
 * The one socket. It dials `/ws` with the versioned subprotocol, sends `rt.hello` as the first
 * frame, hands the connection to the boot for its catch-up (`rt.bootstrap` or `rt.sync`, the
 * boot's choice — protocol.md §2.8), and from then on turns frames into events and requests into
 * replies. Everything about staying connected lives here: the backoff, the application-level
 * ping that keeps the server's read deadline fed and detects a half-open socket (a browser cannot
 * see protocol pings), the immediate drop on `offline` and redial on `online` and on the tab
 * becoming visible, the five-minute hidden rule, the drain delay, and the three close codes after
 * which the client does not dial again (protocol.md §2.1, §2.10) — one of which, the
 * protocol-version mismatch, first fetches matching assets once (§6).
 *
 * Requests carry a client-minted id, strictly increasing per connection; a reply resolves the
 * request by id, an `err` frame rejects it with the server's error shape, a close rejects every
 * request still in flight — the outbox re-dispatches after the next catch-up, never before — and a
 * request nobody answered by its deadline is rejected too, so that nothing above this file can wait
 * for ever on a promise.
 *
 * A visitor by link (DV W-1 D29, §3.6) dials through the same socket with the `visitor` option: its
 * hello carries the instance's double-submit token, since it has no session to derive one from; it
 * catches nothing up, because the server opens its connection at the head and refuses it the
 * instance's log; an `rt.resync`, which it cannot answer with a sync, is a redial; and a 4401 ends it
 * as it ends a member's. Its away states and its hidden keep are the same reporter's.
 *
 * Two things leave this file: `SocketStatus`, which the boot turns into the model's one connection
 * word, and `ConnectionDetail`, the one fact about *why* the socket is down that no state word can
 * carry — read directly by the pill that reports it.
 */

import { readSessionValue, storageKey, writeSessionValue } from "../../lib/storage.ts"
import {
	CloseGoingAway,
	CloseOriginOrCSRF,
	CloseProtocolVersion,
	CloseRateLimited,
	CloseSessionInvalid,
	eventFromEnvelope,
	KindErr,
	KindEvt,
	KindReq,
	ProtocolVersion,
	Subprotocol,
	type Envelope,
	type HelloResult,
	type ReplyData,
	type RequestData,
	type RequestType,
	type Error as WireErrorShape,
	type WireEvent,
} from "../wire/index.ts"
import { WireError } from "./api.ts"
import { Backoff } from "./backoff.ts"
import { focusReporter, type FocusReport, type FocusReporter } from "./focusReporter.ts"
import { browserKeepLocks, KeeperPlace, type KeepLocks } from "./hiddenKeep.ts"

/**
 * Nothing **sent** for this long → the client sends `rt.ping` (protocol.md §2.1). The clock runs on
 * the client's own frames because those are what the server's read deadline counts: a reader who
 * only receives would otherwise be hung up on while the events keep coming. It is the fallback for
 * a hello reply that names no read deadline; one that does is answered with a third of it.
 */
export const SEND_IDLE_PING_MS = 20_000

/** The share of the server's read deadline one ping interval may use: two may be lost. */
const PINGS_PER_READ_DEADLINE = 3

/** A server that named an absurd deadline does not get a client that does nothing but ping. */
const MIN_PING_INTERVAL_MS = 1000

/** No `res rt.ping` within this → the socket is half-open; close and redial. */
export const PING_TIMEOUT_MS = 10_000

/**
 * No reply within this → the request is rejected as retryable (protocol.md §2.10). Generous for a
 * server that answers in milliseconds, and deliberately not a verdict on the socket: the ping above
 * is what decides whether the connection lives, this only hands a lost settlement back to a caller
 * who would otherwise wait on it for the life of the tab.
 *
 * It is the number for *every* request, `rt.bootstrap` and `rt.sync` included, large as their
 * replies are. A longer budget is one the keepalive above does not grant: a ping falls due
 * `SEND_IDLE_PING_MS` after the last frame this client sent and gives up on the socket
 * `PING_TIMEOUT_MS` later, whatever a request is still waiting for, so a deadline above that
 * ceiling could never be reached. The server reads and dispatches on separate goroutines and
 * answers `rt.ping` while a handler runs (protocol.md §2.2), so a slow catch-up is no longer
 * evidence about the connection at all — which is exactly what makes this deadline the caller's
 * business and not the socket's.
 */
export const REQUEST_TIMEOUT_MS = 30_000

function deadlineFor(type: string): number {
	return type === "rt.ping" ? PING_TIMEOUT_MS : REQUEST_TIMEOUT_MS
}

/**
 * A tab hidden this long closes its socket and redials on return (protocol.md §2.10), unless its
 * page asks to keep it and a keeper's place is free (`hiddenKeep.ts`): an owner's AI chat open in a
 * background tab is passive use that keeps its container for hours, a document keeps its face in the
 * band for at most the hour its page asks (`DOC_HIDDEN_KEEP_MS`), and the server sees either open only
 * while the tab's connection says so. A keeping tab looks again after the same time, so a page that
 * stopped asking while hidden, or whose hour is over, lets the socket go within five minutes.
 */
export const HIDDEN_CLOSE_MS = 5 * 60_000

/**
 * Where the connection stands. `catchup` is the window between the hello reply and the end of
 * `rt.sync`/`rt.bootstrap`, during which the outbox holds; `offline` and `draining` carry the time
 * of the next dial for the banner's countdown.
 */
export type SocketStatus =
	| { kind: "idle" }
	| { kind: "connecting" }
	| { kind: "catchup" }
	| { kind: "open" }
	| { kind: "offline"; retryAt: number }
	| { kind: "draining"; retryAt: number }
	| { kind: "revoked" }
	/** Closed by a code the client must not answer with a redial (4403, 4409). */
	| { kind: "stopped"; code: number }

/**
 * Why the socket is not open, where the one word the model carries (`ConnectionState`) cannot say it.
 *
 * There is exactly one reason worth a sentence of its own, and it is the one outage a reader causes
 * themselves: `realtime.connect` is twenty a minute per account (`admission/buckets.go`), which a
 * habit of reloading reaches in a minute — and the pill then blamed the network for it, with no way
 * for the reader to know that waiting would fix it (X-B1 P13). It reaches the client twice: as the
 * refusal of `rt.hello` (`rateLimited`, with the server's own `retryAfterMs`), and as close
 * **4429** once a bucket has been emptied three times on one connection.
 *
 * It lives beside the socket rather than in the model because it is a fact about the transport and
 * one pill is the only thing in the product that reads it; putting it in the model would mean a
 * field, a reducer and a wire shape for a sentence. `retryAt` is the dial this client has actually
 * scheduled (the backoff's), so the countdown is a promise the client itself keeps.
 */
export interface ConnectionDetail {
	reason: "none" | "rateLimited"
	/** A clock reading, or 0 while no redial is scheduled. */
	retryAt: number
}

const NO_DETAIL: ConnectionDetail = { reason: "none", retryAt: 0 }

let detail: ConnectionDetail = NO_DETAIL
const watchers = new Set<() => void>()

/** What is known about the connection beyond its state; one stable object per distinct answer. */
export function connectionDetail(): ConnectionDetail {
	return detail
}

/** Subscribes to it (`useSyncExternalStore`); the returned function unsubscribes. */
export function watchConnectionDetail(onChange: () => void): () => void {
	watchers.add(onChange)
	return () => watchers.delete(onChange)
}

function publishDetail(next: ConnectionDetail): void {
	if (next.reason === detail.reason && next.retryAt === detail.retryAt) return
	detail = next.reason === "none" && next.retryAt === 0 ? NO_DETAIL : next
	for (const watcher of watchers) watcher()
}

/** The subset of the browser's WebSocket the socket uses; a test hands in a fake. */
export interface SocketLike {
	readyState: number
	onopen: ((ev: unknown) => void) | null
	onmessage: ((ev: { data: unknown }) => void) | null
	onclose: ((ev: { code: number; reason: string }) => void) | null
	onerror: ((ev: unknown) => void) | null
	send(data: string): void
	close(code?: number, reason?: string): void
}

export type SocketConstructor = new (url: string, protocols: string) => SocketLike

export interface SocketDeps {
	/** The session's synchroniser token, read at every dial — it may have been re-delivered. */
	csrf: () => string
	/** `web/<sha>`: the build the server compares against its minimum (protocol.md §6). */
	client: string
	locale?: string
	/**
	 * Runs after `rt.hello`: the boot's bootstrap-or-sync. Events are held server-side until it replies.
	 * A visitor's socket has none (`visitor`).
	 */
	catchUp?: (socket: Socket) => Promise<void>
	/**
	 * The connection of a visitor by link, who has no session (W-1 D29): `csrf` answers the
	 * double-submit token, nothing is caught up, and an `rt.resync` is a redial.
	 */
	visitor?: boolean
	onHello?: (result: HelloResult) => void
	onEvent: (event: WireEvent) => void
	onStatus?: (status: SocketStatus) => void
	/** The tab's focus reporter unless a test says otherwise. */
	focus?: FocusReporter
	/** The document's WebSocket unless a test says otherwise. */
	WebSocket?: SocketConstructor
	/** The places of hidden keepers: the browser's lock manager by default, `null` for none. */
	locks?: KeepLocks | null
	/** Fetches the page again after a protocol mismatch; the document's own reload by default. */
	reload?: () => void
	url?: string
	random?: () => number
	now?: () => number
}

interface Pending {
	type: string
	resolve: (data: unknown) => void
	reject: (error: WireError) => void
	/** Rejects the request when no reply came; cleared by whatever settles it first. */
	deadline: ReturnType<typeof setTimeout>
}

const OPEN = 1

/**
 * After a protocol-version mismatch the client fetches matching assets once, and only once inside
 * this window: a reload that keeps meeting the same mismatch would loop (protocol.md §6).
 */
export const RELOAD_GUARD_MS = 60_000

const RELOAD_GUARD_KEY = storageKey("protocolReload")

/** Close codes after which the client never dials again on its own (protocol.md §2.1). */
const FINAL_CLOSE_CODES: ReadonlySet<number> = new Set([
	CloseSessionInvalid,
	CloseOriginOrCSRF,
	CloseProtocolVersion,
])

export class Socket {
	private ws: SocketLike | undefined
	private nextId = 1
	private readonly pending = new Map<number, Pending>()
	private readonly backoff: Backoff
	private status: SocketStatus = { kind: "idle" }
	private redialTimer: ReturnType<typeof setTimeout> | undefined
	private pingTimer: ReturnType<typeof setTimeout> | undefined
	private pingIntervalMs = SEND_IDLE_PING_MS
	private hiddenTimer: ReturnType<typeof setTimeout> | undefined
	/** When the tab was last hidden, by `now`; a bounded keep counts its hour from here. */
	private hiddenAt = 0
	private drainingAfterMs: number | undefined
	/** Set by a refused hello or a 4429 close, cleared by a connection that opens. */
	private rateLimited = false
	private stopped = false
	private readonly now: () => number
	private readonly deps: SocketDeps
	private readonly focusReporter: FocusReporter
	private readonly keeper: KeeperPlace

	constructor(deps: SocketDeps) {
		this.deps = deps
		this.focusReporter = deps.focus ?? focusReporter
		this.keeper = new KeeperPlace(
			deps.locks === undefined ? browserKeepLocks() : deps.locks,
			this.onPlaceLost,
		)
		this.backoff = new Backoff(deps.random)
		this.now = deps.now ?? Date.now
		if (document.visibilityState === "hidden") this.hiddenAt = this.now()
		window.addEventListener("online", this.onOnline)
		window.addEventListener("offline", this.onOffline)
		document.addEventListener("visibilitychange", this.onVisibility)
	}

	currentStatus(): SocketStatus {
		return this.status
	}

	/** Dials now. Harmless while a connection exists. */
	connect(): void {
		this.stopped = false
		this.clearRedial()
		if (this.ws) return
		this.dial()
	}

	/** Closes for good: no redial until `connect()` is called again. In-flight requests fail. */
	stop(): void {
		this.stopped = true
		this.clearRedial()
		this.keeper.giveBack()
		this.ws?.close(1000, "client")
		this.dropConnection()
		this.setStatus({ kind: "idle" })
	}

	/** Releases the document listeners; the socket is unusable afterwards. */
	dispose(): void {
		this.stop()
		window.removeEventListener("online", this.onOnline)
		window.removeEventListener("offline", this.onOffline)
		document.removeEventListener("visibilitychange", this.onVisibility)
	}

	/**
	 * One request, one settlement — `res` or `ack` resolves; `err`, a close and the deadline reject.
	 * A frame the browser refuses to write is a request that met no connection, and leaves no entry
	 * behind for a reply that cannot come.
	 */
	send<T extends RequestType>(type: T, data: RequestData<T>): Promise<ReplyData<T>> {
		const ws = this.ws
		if (!ws || ws.readyState !== OPEN) return Promise.reject(disconnected())
		const id = this.nextId++
		const frame: Envelope = { v: ProtocolVersion, kind: KindReq, id, type, data }
		const text = JSON.stringify(frame)
		try {
			ws.send(text)
		} catch {
			// A socket the browser will not write to is one nothing can be asked over again, so it is
			// given up on here rather than only refused: the keepalive's own write is one of these
			// writes and clears the cadence before it, so a refusal that was only reported would leave
			// a tab with nothing armed at all — no ping, no deadline, no redial — saying "open".
			this.hangUp(ws, "write failed")
			return Promise.reject(disconnected())
		}
		this.restartPingClock()
		return new Promise<ReplyData<T>>((resolve, reject) => {
			const deadline = setTimeout(() => this.expire(id), deadlineFor(type))
			this.pending.set(id, { type, resolve: (d) => resolve(d as ReplyData<T>), reject, deadline })
		})
	}

	/**
	 * The scope this tab has on screen, or its page shows behind a hidden tab, or nothing
	 * (`rt.focus`), with how long the tab has been away once it is; a reply is not awaited. Pages do
	 * not call it: they tell the focus reporter, which calls it after every catch-up and on every
	 * change (`focusReporter.ts`).
	 */
	focus(report: FocusReport | null): void {
		if (!this.ws || this.ws.readyState !== OPEN) return
		const data =
			report === null
				? {}
				: {
						kind: report.kind,
						id: report.id,
						...(report.hidden === true ? { hidden: true } : {}),
						...(report.away === undefined ? {} : { away: report.away }),
					}
		this.send("rt.focus", data).catch(() => {
			// A refused focus changes nothing the user can see.
		})
	}

	// ---- dialling and the handshake ----

	private dial(): void {
		const Ctor = this.deps.WebSocket ?? (WebSocket as unknown as SocketConstructor)
		const url =
			this.deps.url ?? `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`
		this.setStatus({ kind: "connecting" })
		let ws: SocketLike
		try {
			ws = new Ctor(url, Subprotocol)
		} catch {
			this.scheduleRedial()
			return
		}
		this.ws = ws
		this.nextId = 1
		ws.onopen = () => {
			void this.handshake(ws)
		}
		ws.onmessage = (ev) => {
			if (this.ws === ws) this.onFrame(ev.data)
		}
		ws.onclose = (ev) => {
			if (this.ws === ws) this.onClose(ev.code)
		}
		ws.onerror = null
	}

	private async handshake(ws: SocketLike): Promise<void> {
		try {
			const hello = await this.send("rt.hello", {
				protocol: ProtocolVersion,
				client: this.deps.client,
				csrf: this.deps.csrf(),
				...(this.deps.locale === undefined ? {} : { locale: this.deps.locale }),
			})
			if (this.ws !== ws) return
			// The clock the hello itself started ran on the previous connection's number.
			this.pingIntervalMs = pingIntervalFor(hello)
			this.restartPingClock()
			this.deps.onHello?.(hello)
			// The grace and the quiet after which this tab is away are the server's numbers.
			this.focusReporter.useLimits(hello.limits)
			this.setStatus({ kind: "catchup" })
			// A visitor's connection opened at the head: there is nothing before it to catch up.
			if (this.deps.visitor !== true) await this.deps.catchUp?.(this)
			if (this.ws !== ws) return
			// The server holds no focus for a new connection; what is on screen is said again here,
			// after the catch-up, so no frame for a watcher is missed between the two.
			this.focusReporter.connected((report) => this.focus(report))
			this.backoff.reset()
			this.rateLimited = false
			// A connection made while the tab is hidden — a redial after a drain or a blip that
			// outlived the last look — is looked at like any other.
			this.armHidden()
			this.setStatus({ kind: "open" })
		} catch (failure) {
			// A refused hello or a failed catch-up: the server closes with its code, or the next
			// dial tries again. Nothing to do here but let onClose decide — except to remember the
			// one refusal the reader can act on, because the close that follows it is an ordinary
			// one and carries no trace of the reason (`ConnectionDetail`).
			if (failure instanceof WireError && failure.code === "rateLimited") this.rateLimited = true
			if (this.ws === ws && ws.readyState === OPEN) ws.close(1000, "handshake")
		}
	}

	// ---- frames ----

	private onFrame(raw: unknown): void {
		if (typeof raw !== "string") return
		let env: Envelope
		try {
			env = JSON.parse(raw) as Envelope
		} catch {
			return
		}
		if (env.kind === KindEvt) {
			const event = eventFromEnvelope(env)
			if (event.type === "rt.draining") this.drainingAfterMs = event.data.reconnectAfterMs
			// The server dropped frames a visitor's connection could not take. It cannot ask for them
			// (`rt.sync` is refused it), so it dials again and opens at the head, and the document's
			// own catch-up on the new connection brings the text (W-1 §3.6).
			if (event.type === "rt.resync" && this.deps.visitor === true) {
				if (this.ws) this.hangUp(this.ws, "resync")
				return
			}
			this.deps.onEvent(event)
			return
		}
		if (env.id === undefined) return
		// A reply to a request that has already met its deadline finds nothing here, and is dropped.
		const pending = this.pending.get(env.id)
		if (!pending) return
		this.pending.delete(env.id)
		clearTimeout(pending.deadline)
		if (env.kind === KindErr) pending.reject(new WireError(0, env.data as WireErrorShape))
		else pending.resolve(env.data)
	}

	/**
	 * The deadline of one request passed. The caller hears a retryable failure, which for every
	 * write the outbox sends is safe to act on: a repeat carries the same `clientMsgId` and the
	 * server answers it with the original ack (protocol.md §2.3). An unanswered *ping* is more than
	 * a slow request — it is the proof that the socket is half-open, so the connection goes with it.
	 */
	private expire(id: number): void {
		const pending = this.pending.get(id)
		if (!pending) return
		this.pending.delete(id)
		pending.reject(timedOut())
		if (pending.type === "rt.ping" && this.ws) this.hangUp(this.ws, "ping timeout")
	}

	private onClose(code: number): void {
		this.dropConnection()
		if (code === CloseRateLimited) this.rateLimited = true
		if (this.stopped) return
		// No redial follows these: a hidden tab gives its keeper's place back, or a signed-out tab
		// would hold it until it is shown or closed.
		if (code === CloseSessionInvalid) {
			this.keeper.giveBack()
			this.setStatus({ kind: "revoked" })
			return
		}
		if (code === CloseProtocolVersion) this.reloadForNewAssets()
		if (FINAL_CLOSE_CODES.has(code)) {
			this.keeper.giveBack()
			this.setStatus({ kind: "stopped", code })
			return
		}
		if (code === CloseGoingAway && this.drainingAfterMs !== undefined) {
			const delay = this.drainingAfterMs
			this.drainingAfterMs = undefined
			this.scheduleRedial(delay, "draining")
			return
		}
		this.scheduleRedial()
	}

	/**
	 * The server speaks a protocol this build does not: the assets are older than the server, so the
	 * page is fetched again — once. The guard is the tab's, because a reload that met the same
	 * mismatch again would loop; when it holds, the status stays `stopped` and the shell offers the
	 * reload to the reader instead (protocol.md §6).
	 */
	private reloadForNewAssets(): void {
		const last = Number(readSessionValue(RELOAD_GUARD_KEY))
		const now = this.now()
		if (Number.isFinite(last) && last > 0 && now - last < RELOAD_GUARD_MS) return
		writeSessionValue(RELOAD_GUARD_KEY, String(now))
		const reload = this.deps.reload ?? (() => globalThis.location.reload())
		reload()
	}

	/** Forgets the connection and fails every request still waiting for a reply. */
	private dropConnection(): void {
		this.stopPingClock()
		this.focusReporter.disconnected()
		const ws = this.ws
		this.ws = undefined
		if (ws) {
			ws.onopen = null
			ws.onmessage = null
			ws.onclose = null
		}
		const waiting = [...this.pending.values()]
		this.pending.clear()
		for (const p of waiting) {
			clearTimeout(p.deadline)
			p.reject(disconnected())
		}
	}

	/**
	 * Closes a socket the client itself has given up on and takes the ordinary redial path at once,
	 * without waiting for a close event that a dead peer may never let the browser deliver.
	 */
	private hangUp(ws: SocketLike, reason: string): void {
		ws.close(1000, reason)
		this.onClose(1006)
	}

	// ---- reconnecting ----

	private scheduleRedial(delayMs?: number, kind: "offline" | "draining" = "offline"): void {
		this.clearRedial()
		const delay = delayMs ?? this.backoff.next()
		this.setStatus({ kind, retryAt: this.now() + delay })
		this.redialTimer = setTimeout(() => {
			this.redialTimer = undefined
			if (!this.stopped && !this.ws) this.dial()
		}, delay)
	}

	private clearRedial(): void {
		if (this.redialTimer !== undefined) clearTimeout(this.redialTimer)
		this.redialTimer = undefined
	}

	private readonly onOnline = () => {
		if (this.status.kind === "offline") this.connect()
	}

	/**
	 * The browser knows the network is gone long before a timer does: a socket that is not written
	 * to is only found dead by the ping and its timeout, which is up to thirty seconds of a tab
	 * that looks perfectly connected. The `offline` event closes it at once, so the banner appears
	 * when the network goes, and `online` above redials.
	 */
	private readonly onOffline = () => {
		const ws = this.ws
		if (this.stopped || !ws) return
		this.hangUp(ws, "offline")
	}

	private readonly onVisibility = () => {
		if (document.visibilityState === "hidden") {
			this.hiddenAt = this.now()
			this.armHidden()
			return
		}
		if (this.hiddenTimer !== undefined) clearTimeout(this.hiddenTimer)
		this.hiddenTimer = undefined
		this.keeper.giveBack()
		if (this.status.kind === "idle" || this.status.kind === "offline") this.connect()
	}

	/** Starts the hidden tab's clock, once (`HIDDEN_CLOSE_MS`). */
	private armHidden(): void {
		if (document.visibilityState !== "hidden" || this.hiddenTimer !== undefined) return
		this.hiddenTimer = setTimeout(this.lookHidden, HIDDEN_CLOSE_MS)
	}

	/**
	 * The hidden tab's look: the socket goes, unless the page asks to keep it, its keep has not run
	 * out, and a keeper's place it may take is free — a document's the last only. A look that finds no
	 * connection — a redial on its way — leaves it to the next look, or to the redial's own
	 * `armHidden`; with no redial to come (stopped, signed out, a final close) there is nothing to look
	 * at again.
	 */
	private readonly lookHidden = () => {
		this.hiddenTimer = undefined
		const ws = this.ws
		if (!ws) {
			const redials =
				!this.stopped && this.status.kind !== "revoked" && this.status.kind !== "stopped"
			if (redials) this.armHidden()
			return
		}
		const keep = this.focusReporter.keepsWhileHidden()
		if (keep === null || this.now() - this.hiddenAt >= keep.forMs) {
			this.closeHidden(ws)
			return
		}
		void this.keeper.take(keep.lastPlaceOnly ? "document" : "page").then((kept) => {
			if (kept) this.armHidden()
			else this.closeHidden(ws)
		})
	}

	/**
	 * A hidden AI chat took this tab's place (a document's, `hiddenKeep.ts`): the tab looks now rather
	 * than at its next look, and with no place left its socket goes.
	 */
	private readonly onPlaceLost = () => {
		if (this.hiddenTimer === undefined) return
		clearTimeout(this.hiddenTimer)
		this.lookHidden()
	}

	private closeHidden(ws: SocketLike): void {
		if (this.ws !== ws || document.visibilityState !== "hidden") return
		this.keeper.giveBack()
		// Deliberate: the redial waits for the tab to come back, not for the schedule.
		this.stopped = true
		ws.close(1000, "hidden")
		this.dropConnection()
		this.setStatus({ kind: "idle" })
	}

	// ---- liveness ----

	/**
	 * Every frame sent restarts the clock, the ping included — so the cadence keeps itself going,
	 * and a client that is talking anyway sends no ping at all.
	 */
	private restartPingClock(): void {
		if (this.pingTimer !== undefined) clearTimeout(this.pingTimer)
		this.pingTimer = setTimeout(() => this.ping(), this.pingIntervalMs)
	}

	private stopPingClock(): void {
		if (this.pingTimer !== undefined) clearTimeout(this.pingTimer)
		this.pingTimer = undefined
	}

	/**
	 * The answer is awaited like any other reply, under the ping's own short deadline (`expire`).
	 * A socket that is no longer open when the ping falls due — the browser is still closing it, and
	 * over a dead link that can take as long as it likes — cannot carry one: it is given up on,
	 * because leaving quietly would leave nothing armed and a tab that says "open" for ever.
	 */
	private ping(): void {
		this.pingTimer = undefined
		const ws = this.ws
		if (!ws) return
		if (ws.readyState !== OPEN) {
			this.hangUp(ws, "not open")
			return
		}
		this.send("rt.ping", {}).catch(() => {
			// The deadline or the close that rejected it has already dealt with the connection.
		})
	}

	private setStatus(status: SocketStatus): void {
		this.status = status
		// The detail is published with the state it belongs to and never on its own: a reader who is
		// waiting out a rate limit is `offline` with a dial in the diary, and every other state has
		// nothing to add.
		const waiting = status.kind === "offline" || status.kind === "draining"
		publishDetail({
			reason: this.rateLimited && waiting ? "rateLimited" : "none",
			retryAt: waiting ? status.retryAt : 0,
		})
		this.deps.onStatus?.(status)
	}
}

/** The error every in-flight request fails with when the connection goes (protocol.md §2.10). */
export function disconnected(): WireError {
	return new WireError(0, { code: "unavailable", key: "error.rt.disconnected" })
}

/** The error a request fails with when its deadline passed without a reply (protocol.md §2.10). */
function timedOut(): WireError {
	return new WireError(0, { code: "unavailable", key: "error.rt.timeout" })
}

/**
 * How long the client may stay silent on this connection: a third of the read deadline the server
 * says it enforces, so the two numbers cannot drift apart. `readDeadlineMs` is read as optional
 * because a server older than the field does not send it.
 */
function pingIntervalFor(hello: HelloResult): number {
	const { readDeadlineMs } = hello
	if (typeof readDeadlineMs !== "number" || !(readDeadlineMs > 0)) return SEND_IDLE_PING_MS
	return Math.max(MIN_PING_INTERVAL_MS, Math.floor(readDeadlineMs / PINGS_PER_READ_DEADLINE))
}
